import useMapEvents from '../useMapEvents';
import { useRef, useState } from 'react';

import {
    DEFAULT_CENTER,
    DEFAULT_DRAWING_MODE,
    DEFAULT_DRAWING_TOOL,
    DEFAULT_POINT_MODE,
    POINT_MODE
} from './constants';
import type { DrawingMode, DrawingTool, Feature, Polygon, Position, UseMapState } from './types';

const useMapState = (): UseMapState => {
    const { eventFactory, publish } = useMapEvents();
    const { createMapVertexChangedEvent } = eventFactory;

    const [drawingTool, setDrawingTool] = useState<DrawingTool>(DEFAULT_DRAWING_TOOL);
    const [drawingMode, setDrawingMode] = useState<DrawingMode>(DEFAULT_DRAWING_MODE);
    const [pointMode, setPointMode] = useState<POINT_MODE>(DEFAULT_POINT_MODE);
    const [mapErrors, setMapErrors] = useState<Error[]>([]);
    const [selectedFeature, setSelectedFeature] = useState<Feature<Polygon>>();
    const [isPointSelected, setIsPointSelected] = useState<boolean>(false);
    const [isDrawing, setIsDrawing] = useState<boolean>(false);

    const mapCenterRef = useRef<Position>(DEFAULT_CENTER);
    const mousePositionRef = useRef<Position>(DEFAULT_CENTER);

    const publishVertexUpdate = () => {
        const vertexPositionMap: Record<POINT_MODE, Position> = {
            [POINT_MODE.RETICLE]: mapCenterRef.current,
            [POINT_MODE.TOUCH]: mousePositionRef.current
        };

        publish(createMapVertexChangedEvent(vertexPositionMap[pointMode]));
    };

    const setMapCenter = (mapCenter: Position) => {
        mapCenterRef.current = mapCenter;
        publishVertexUpdate();
    };

    const setMousePosition = (mousePosition: Position) => {
        mousePositionRef.current = mousePosition;
        publishVertexUpdate();
    };

    return {
        drawingMode,
        drawingTool,
        isDrawing,
        isPointSelected,
        mapErrors,
        pointMode,
        selectedFeature,
        setDrawingMode,
        setDrawingTool,
        setIsDrawing,
        setIsPointSelected,
        setMapCenter,
        setMapErrors,
        setMousePosition,
        setPointMode,
        setSelectedFeature
    };
};

export default useMapState;
